import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Loader2, Star, ArrowLeft, Sparkles, Gift, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { format } from "date-fns";
import { capture as posthogCapture } from "@/services/posthog";
import { useRef } from "react";
import FallingArtObjects from "@/components/thank-you/FallingArtObjects";
import { useHostProfiles, type HostProfile } from "@/hooks/useHostProfiles";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Check, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useFeedbackCandidates, type FeedbackCandidate } from "@/hooks/useFeedbackCandidates";

type ValueKey =
  | "art_culture"
  | "hidden_gem"
  | "meet_people"
  | "learning"
  | "social_after"
  | "topic_intro";

const VALUE_OPTIONS: { key: ValueKey; label: string }[] = [
  { key: "art_culture", label: "Experiencing art & culture and feeling inspired" },
  { key: "hidden_gem", label: "Hidden gem that I didn't know about or can't access on my own" },
  { key: "meet_people", label: "Meet and connect with people" },
  { key: "learning", label: "Learning and building my knowledge" },
  { key: "social_after", label: "Social gathering after the event" },
  { key: "topic_intro", label: "Good intro to a topic I'm not familiar with" },
];

type FormState = {
  nps: number | null;
  rating_curation: number | null;
  rating_organisation: number | null;
  rating_host: number | null;
  rating_group: number | null;
  ranking: ValueKey[];
  open_comment: string;
  selected_host_id: string | null;
};

const EMPTY: FormState = {
  nps: null,
  rating_curation: null,
  rating_organisation: null,
  rating_host: null,
  rating_group: null,
  ranking: [],
  open_comment: "",
  selected_host_id: null,
};

const draftKey = (eventId: string) => `feedback-draft-${eventId}`;

const rebookUrl = (path: string, content: string) =>
  `${path}?utm_source=feedback&utm_medium=post_event&utm_campaign=rebook&utm_content=${content}`;

export default function Feedback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: profile, isLoading: profileLoading } = useProfile();

  const [selected, setSelected] = useState<FeedbackCandidate | null>(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{
    is_first_timer: boolean;
    code_status: string;
    code: string | null;
    valid_until: string | null;
  } | null>(null);

  const userId = profile?.id;

  // Redirect to /auth if not signed in.
  useEffect(() => {
    if (!profileLoading && !userId) {
      navigate(`/auth?redirectTo=${encodeURIComponent("/feedback")}`);
    }
  }, [profileLoading, userId, navigate]);

  const candidatesQuery = useFeedbackCandidates(userId);

  // Auto-select when there's a single candidate, and honour ?ticket= override.
  useEffect(() => {
    if (!candidatesQuery.data || selected) return;
    const paramTicket = searchParams.get("ticket");
    if (paramTicket) {
      const match = candidatesQuery.data.find((c) => c.ticket_id === paramTicket);
      if (match) {
        setSelected(match);
        return;
      }
    }
    if (candidatesQuery.data.length === 1) setSelected(candidatesQuery.data[0]);
  }, [candidatesQuery.data, selected, searchParams]);

  // Load draft + existing submission when a candidate is picked.
  useEffect(() => {
    if (!selected || !userId) return;
    const draft = localStorage.getItem(draftKey(selected.event_id));
    if (draft) {
      try {
        setForm({ ...EMPTY, ...JSON.parse(draft) });
      } catch {
        setForm(EMPTY);
      }
    }
    // Prefill from existing feedback if present.
    (async () => {
      const { data } = await supabase
        .from("event_feedback")
        .select(
          "nps, rating_curation, rating_organisation, rating_host, rating_group, value_ranking, open_comment, host_id",
        )
        .eq("event_id", selected.event_id)
        .eq("user_id", userId)
        .maybeSingle();
      if (data) {
        setForm({
          nps: data.nps ?? null,
          rating_curation: data.rating_curation ?? null,
          rating_organisation: data.rating_organisation ?? null,
          rating_host: data.rating_host ?? null,
          rating_group: data.rating_group ?? null,
          ranking: (data.value_ranking ?? []) as ValueKey[],
          open_comment: data.open_comment ?? "",
          selected_host_id: (data as any).host_id ?? null,
        });
      }
    })();
  }, [selected, userId]);

  // Persist draft on any form change.
  useEffect(() => {
    if (!selected) return;
    localStorage.setItem(draftKey(selected.event_id), JSON.stringify(form));
  }, [form, selected]);

  const hasHost = !!selected?.host_id;
  // Co-hosted strolls split the group: ask which host guided them first.
  const isCoHosted =
    !!selected?.host_id &&
    !!selected?.secondary_host_id &&
    selected.host_id !== selected.secondary_host_id;
  const offset = isCoHosted ? 1 : 0;
  const totalSteps = 4 + offset;
  const logical = step - offset; // -1 = guide picker

  const { data: hostMap } = useHostProfiles(
    isCoHosted ? [selected?.host_id, selected?.secondary_host_id] : [selected?.host_id],
  );
  const chosenHostId = isCoHosted
    ? form.selected_host_id === selected?.host_id || form.selected_host_id === selected?.secondary_host_id
      ? form.selected_host_id
      : null
    : selected?.host_id ?? null;
  const chosenHost = chosenHostId ? hostMap?.[chosenHostId] : undefined;

  const canAdvance = useMemo(() => {
    switch (logical) {
      case -1:
        return !!chosenHostId;
      case 0:
        return form.nps !== null;
      case 1:
        return (
          form.rating_curation !== null &&
          form.rating_organisation !== null &&
          form.rating_group !== null &&
          (!hasHost || form.rating_host !== null)
        );
      case 2:
        return form.ranking.length === 3;
      case 3:
        return true;
      default:
        return false;
    }
  }, [logical, form, hasHost, chosenHostId]);

  async function submit() {
    if (!selected || !userId || submitting) return;
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("submit-event-feedback", {
        body: {
          event_id: selected.event_id,
          ticket_id: selected.ticket_id,
          nps: form.nps,
          rating_curation: form.rating_curation,
          rating_organisation: form.rating_organisation,
          rating_host: hasHost ? form.rating_host : null,
          rating_group: form.rating_group,
          value_ranking: form.ranking,
          open_comment: form.open_comment.trim() || null,
          host_id: chosenHostId,
        },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error(JSON.stringify((data as any).error));
      const res = data as {
        is_first_timer: boolean;
        code_status: string;
        code: string | null;
        valid_until: string | null;
      };
      setResult(res);
      localStorage.removeItem(draftKey(selected.event_id));
      posthogCapture("feedback_submitted", {
        event_id: selected.event_id,
        nps: form.nps,
        is_first_timer: res.is_first_timer,
        code_issued: res.code_status === "issued",
      });
      toast.success("Thanks — your feedback is saved");
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  if (profileLoading || candidatesQuery.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (result) return <ThankYouScreen result={result} nps={form.nps ?? 10} />;

  const candidates = candidatesQuery.data ?? [];

  if (candidates.length === 0) {
    return (
      <div className="min-h-screen bg-background px-4 py-12">
        <Card className="max-w-md mx-auto p-8 text-center space-y-4">
          <h1 className="text-2xl font-semibold">No recent events to review</h1>
          <p className="text-muted-foreground">
            We couldn't find a recent Nimi event linked to your account.
          </p>
          <Button asChild>
            <Link to="/events">Browse events</Link>
          </Button>
        </Card>
      </div>
    );
  }

  if (!selected) {
    return (
      <div className="min-h-screen bg-background px-4 py-8">
        <div className="max-w-md mx-auto space-y-4">
          <h1 className="text-2xl font-semibold">Which event are you reviewing?</h1>
          <p className="text-sm text-muted-foreground">Pick the one you attended.</p>
          <div className="space-y-3">
            {candidates.map((c) => (
              <Button
                key={c.ticket_id}
                type="button"
                variant="ghost"
                onClick={() => setSelected(c)}
                className="h-auto w-full justify-start gap-3 rounded-lg bg-card p-4 text-left shadow-md transition-shadow hover:bg-card hover:shadow-lg"
              >
                {c.event_image && (
                  <img
                    src={c.event_image}
                    alt=""
                    className="w-16 h-16 rounded object-cover shrink-0"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-medium">{c.event_title}</p>
                    {c.submitted_at && (
                      <Badge variant="secondary" className="gap-1">
                        Submitted <CheckCircle2 className="h-3 w-3" />
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {format(new Date(c.event_start), "EEE d MMM")}
                  </p>
                  <p className={cn("mt-1 text-xs", c.submitted_at ? "text-muted-foreground" : "font-medium text-primary")}>
                    {c.submitted_at ? "Tap to view or edit" : "Feedback pending →"}
                  </p>
                </div>
              </Button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-4 py-6 pb-24">
      <div className="max-w-md mx-auto space-y-6">
        <div className="flex items-center gap-3">
          {step > 0 ? (
            <button
              onClick={() => setStep(step - 1)}
              className="text-muted-foreground"
              aria-label="Back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          ) : candidates.length > 1 ? (
            <button
              onClick={() => setSelected(null)}
              className="text-muted-foreground"
              aria-label="Change event"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          ) : null}
          <div className="flex-1">
            <p className="text-xs text-muted-foreground">{selected.event_title}</p>
            <Progress value={((step + 1) / totalSteps) * 100} className="h-1 mt-1" />
          </div>
        </div>

        {logical === -1 && selected.secondary_host_id && (
          <GuidePickerStep
            hosts={[selected.host_id!, selected.secondary_host_id].map(
              (id) => hostMap?.[id] ?? { id, first_name: null, last_name: null, avatar_url: null },
            )}
            value={chosenHostId}
            onChange={(id) => setForm({ ...form, selected_host_id: id })}
          />
        )}
        {logical === 0 && <NpsStep value={form.nps} onChange={(v) => setForm({ ...form, nps: v })} />}
        {logical === 1 && (
          <RatingsStep form={form} setForm={setForm} hasHost={hasHost} host={chosenHost} />
        )}
        {logical === 2 && (
          <RankingStep
            ranking={form.ranking}
            onChange={(r) => setForm({ ...form, ranking: r })}
          />
        )}
        {logical === 3 && (
          <OpenCommentStep
            nps={form.nps ?? 10}
            value={form.open_comment}
            onChange={(v) => setForm({ ...form, open_comment: v })}
          />
        )}

        <Button
          className="w-full"
          size="lg"
          disabled={!canAdvance || submitting}
          onClick={() => {
            if (step < totalSteps - 1) setStep(step + 1);
            else submit();
          }}
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Sending…
            </>
          ) : step < totalSteps - 1 ? (
            "Continue"
          ) : (
            "Submit"
          )}
        </Button>
      </div>
    </div>
  );
}

// ── Step components ─────────────────────────────────────────────────────────

function NpsStep({ value, onChange }: { value: number | null; onChange: (v: number) => void }) {
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">How likely are you to recommend this Nimi experience to a friend?</h2>
      <div className="grid grid-cols-11 gap-1">
        {Array.from({ length: 11 }, (_, i) => (
          <button
            key={i}
            onClick={() => onChange(i)}
            className={cn(
              "h-10 rounded text-sm font-medium border transition-colors",
              value === i
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background hover:bg-muted",
            )}
          >
            {i}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>Not at all likely</span>
        <span>Extremely likely</span>
      </div>
    </div>
  );
}

function GuidePickerStep({
  hosts,
  value,
  onChange,
}: {
  hosts: HostProfile[];
  value: string | null;
  onChange: (id: string) => void;
}) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">Who guided your group?</h2>
        <p className="text-sm text-muted-foreground">
          This stroll had two hosts. Pick the one who led your walk so your feedback reaches them.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {hosts.map((h) => {
          const active = value === h.id;
          const name = h.first_name || "Host";
          return (
            <button
              key={h.id}
              onClick={() => onChange(h.id)}
              aria-pressed={active}
              className={cn(
                "relative rounded-lg border p-4 flex flex-col items-center gap-3 transition-colors",
                active ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-muted/50",
              )}
            >
              {active && (
                <span className="absolute top-2 right-2 rounded-full bg-primary text-primary-foreground p-0.5">
                  <Check className="h-3.5 w-3.5" />
                </span>
              )}
              <Avatar className="h-20 w-20">
                {h.avatar_url && <AvatarImage src={h.avatar_url} alt={name} className="object-cover" />}
                <AvatarFallback className="text-lg">
                  {(h.first_name?.[0] ?? "") + (h.last_name?.[0] ?? "") || "H"}
                </AvatarFallback>
              </Avatar>
              <span className="font-medium">{name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RatingsStep({
  form,
  setForm,
  hasHost,
  host,
}: {
  form: FormState;
  setForm: (f: FormState) => void;
  hasHost: boolean;
  host?: HostProfile;
}) {
  const rows: { key: keyof FormState; label: string }[] = [
    { key: "rating_curation", label: "Curation" },
    { key: "rating_organisation", label: "Organisation & logistics" },
    ...(hasHost ? [{ key: "rating_host" as const, label: host?.first_name ? `Host (${host.first_name})` : "Host" }] : []),
    { key: "rating_group", label: "Group dynamic / vibe" },
  ];
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">How was it?</h2>
      <div className="space-y-5">
        {rows.map((row) => (
          <div key={row.key as string}>
            <p className="font-medium mb-2">{row.label}</p>
            <div className="flex gap-1 mt-1">
              {[1, 2, 3, 4, 5].map((n) => {
                const active = (form[row.key] as number | null) !== null && (form[row.key] as number) >= n;
                return (
                  <button
                    key={n}
                    onClick={() => setForm({ ...form, [row.key]: n })}
                    aria-label={`${n} stars`}
                  >
                    <Star
                      className={cn(
                        "h-8 w-8",
                        active ? "fill-primary text-primary" : "text-muted-foreground/40",
                      )}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RankingStep({
  ranking,
  onChange,
}: {
  ranking: ValueKey[];
  onChange: (r: ValueKey[]) => void;
}) {
  const shuffledOptions = useMemo(() => {
    const arr = [...VALUE_OPTIONS];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, []);
  function toggle(key: ValueKey) {
    if (ranking.includes(key)) {
      onChange(ranking.filter((k) => k !== key));
    } else if (ranking.length < 3) {
      onChange([...ranking, key]);
    }
  }
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">What did you value most?</h2>
        <p className="text-sm text-muted-foreground">Tap to rank your top 3.</p>
      </div>
      <div className="space-y-2">
        {shuffledOptions.map((opt) => {
          const rank = ranking.indexOf(opt.key);
          const selected = rank >= 0;
          return (
            <button
              key={opt.key}
              onClick={() => toggle(opt.key)}
              className={cn(
                "w-full text-left rounded-lg p-3 border transition-colors flex items-center gap-3",
                selected
                  ? "border-primary bg-primary/5"
                  : "border-border bg-white hover:bg-muted/50",
              )}
            >
              <div
                className={cn(
                  "w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium shrink-0",
                  selected
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {selected ? rank + 1 : ""}
              </div>
              <span className="text-sm">{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function OpenCommentStep({
  nps,
  value,
  onChange,
}: {
  nps: number;
  value: string;
  onChange: (v: string) => void;
}) {
  const label =
    nps <= 6
      ? "Sorry to hear that — what let you down?"
      : "Anything we could do even better?";
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">{label}</h2>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, 2000))}
        placeholder="Optional"
        className="min-h-[140px]"
      />
      <p className="text-xs text-muted-foreground text-right">{value.length}/2000</p>
    </div>
  );
}

// ── Thank-you ────────────────────────────────────────────────────────────────

function ThankYouScreen({
  result,
  nps,
}: {
  result: {
    is_first_timer: boolean;
    code_status: string;
    code: string | null;
    valid_until: string | null;
  };
  nps: number;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const isFirstTimer =
    result.code_status === "issued" || result.code_status === "existing";

  let heading = "Thanks — your feedback is saved";
  let body: React.ReactNode = "It really helps us keep raising the bar.";
  let icon = <Sparkles className="h-10 w-10 text-primary" />;

  if (result.code_status === "issued" || result.code_status === "existing") {
    icon = <Gift className="h-10 w-10 text-primary" />;
    heading = "Your first-timer offer";
    const validDate = result.valid_until
      ? format(new Date(result.valid_until), "d MMM")
      : null;
    body = (
      <>
        Your 20% off code{" "}
        <span className="font-mono font-semibold">{result.code}</span> is
        auto-applied at checkout{validDate ? ` — valid until ${validDate}` : ""}.
      </>
    );
  } else if (result.code_status === "unmarked_attendance") {
    body = "We'll send your first-timer offer once your attendance is confirmed.";
  } else if (result.code_status === "expired") {
    body = "Your first-timer offer has expired — but there's always another Nimi to explore.";
  }

  const showOffersCta = nps >= 7;

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 relative overflow-hidden">
      <FallingArtObjects cardRef={cardRef} />
      <Card
        ref={cardRef}
        className="w-full max-w-md p-8 text-center space-y-4 relative z-10 animate-scale-in"
      >
        <div className="flex justify-center">{icon}</div>
        <h1 className="text-2xl font-semibold">{heading}</h1>
        <p className="text-muted-foreground">{body}</p>
        <div className="space-y-3 pt-2">
          <Button asChild size="lg" className="w-full">
            <Link to={rebookUrl("/events", isFirstTimer ? "first_timer" : "returning")}>
              <Calendar className="h-4 w-4 mr-2" />
              Book your next event
            </Link>
          </Button>
          <p className="text-xs text-muted-foreground">
            Book early to unlock our early-bird discount.
          </p>
          {showOffersCta && (
            <Button asChild variant="outline" size="lg" className="w-full">
              <Link to={rebookUrl("/offers", "offers")}>Explore Nimi offers</Link>
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
